(function () {
  "use strict";

  // Each entry adds a free line under the trigger product. qtyPerTrigger scales
  // with the trigger's quantity; fixedQty adds that many once, whatever the
  // trigger's quantity. A bundled variant can be shared by several triggers: each
  // trigger gets its own line, told apart by the _bundle_parent property.
  const BUNDLES = [
    {
      // 9 Essential Skills for the Love and Logic Classroom (3rd Edition) - Digital
      // → 9 Essential Skills ... (3rd Edition) - Physical Workbook
      triggerVariantId: 46602246258875,
      bundledVariantId: 48107665522875,
      qtyPerTrigger: 10,
    },
    {
      // 9 Essential Skills for the Love and Logic Classroom (3rd Edition) - Digital
      // → Love and Logic Tote Bag
      triggerVariantId: 46602246258875,
      bundledVariantId: 28394269605965,
      fixedQty: 1,
    },
    {
      // Parenting the Love and Logic Way - Digital
      // → Parenting the Love and Logic Way - Workbook
      triggerVariantId: 48140374409403,
      bundledVariantId: 30949899042893,
      qtyPerTrigger: 10,
    },
    {
      // Parenting the Love and Logic Way - Digital
      // → Love and Logic Tote Bag
      triggerVariantId: 48140374409403,
      bundledVariantId: 28394269605965,
      fixedQty: 1,
    },
  ];
  const CART_URL = (window.routes && window.routes.cart_url) || "/cart";
  const LOG = (...args) =>
    console.log(
      "%c[auto-bundle]",
      "color:#fff;background:#8b5cf6;padding:2px 6px;border-radius:3px;",
      ...args,
    );
  const WARN = (...args) =>
    console.warn(
      "%c[auto-bundle]",
      "color:#fff;background:#f59e0b;padding:2px 6px;border-radius:3px;",
      ...args,
    );
  const ERR = (...args) =>
    console.error(
      "%c[auto-bundle]",
      "color:#fff;background:#ef4444;padding:2px 6px;border-radius:3px;",
      ...args,
    );

  const state = {
    internal: false,
    ready: false,
    nestingUnsupported: false,
    queue: Promise.resolve(),
  };

  const originalFetch = window.fetch.bind(window);

  function getVariantQty(cart, variantId) {
    if (!cart || !cart.items) return 0;
    let total = 0;
    for (const item of cart.items) {
      if (item.variant_id === variantId) total += item.quantity;
    }
    return total;
  }

  function findLineItem(cart, variantId) {
    if (!cart || !cart.items) return null;
    for (const item of cart.items) {
      if (item.variant_id === variantId) return item;
    }
    return null;
  }

  // The cart section matches children on Shopify's own parent relationship and
  // falls back to this property, so it has to stay in step with that fallback
  // lookup in main-cart-items.liquid.
  function getParentPropValue(bundle) {
    return String(bundle.triggerVariantId);
  }

  // The tote bag is bundled with both Digital products, so a line belongs to a
  // bundle only when its _bundle_parent matches too. Lines added before that
  // property existed carry none; they are claimed on the variant alone and then
  // rebuilt as legacy lines.
  function isOurLine(item, bundle) {
    if (
      !item ||
      item.variant_id !== bundle.bundledVariantId ||
      !item.properties ||
      !item.properties._auto_bundle
    )
      return false;
    const parent = item.properties._bundle_parent;
    return !parent || parent === getParentPropValue(bundle);
  }

  function getTargetQty(bundle, triggerQty) {
    if (triggerQty === 0) return 0;
    if (bundle.fixedQty) return bundle.fixedQty;
    return triggerQty * bundle.qtyPerTrigger;
  }

  function isAnyBundleLine(item) {
    return BUNDLES.some((bundle) => isOurLine(item, bundle));
  }

  // The key of the line the workbook hangs off. Shopify nests the free line
  // under it, and that relationship is what renders the workbook as a child line
  // in checkout, on the order status page, in order emails and in the admin order.
  function getParentLineKey(cart, bundle) {
    const parent = findLineItem(cart, bundle.triggerVariantId);
    return parent ? parent.key : null;
  }

  // A line is stale when it predates either half of the current shape: the
  // _bundle_parent property the cart page falls back on, or the nesting itself.
  // Stale lines are rebuilt on sight, so carts from before this change fix
  // themselves on the next page load.
  function isLegacyLine(item, parentKey, bundle) {
    if (!isOurLine(item, bundle)) return false;
    if (item.properties._bundle_parent !== getParentPropValue(bundle))
      return true;
    if (state.nestingUnsupported) return false;
    const relationship = item.parent_relationship;
    return !parentKey || !relationship || relationship.parent_key !== parentKey;
  }

  function summarizeOurLines(cart, bundle) {
    const parentKey = getParentLineKey(cart, bundle);
    let qty = 0;
    let count = 0;
    let legacy = 0;
    (cart.items || []).forEach((item) => {
      if (!isOurLine(item, bundle)) return;
      count++;
      qty += item.quantity;
      if (isLegacyLine(item, parentKey, bundle)) legacy++;
    });
    return { qty, count, legacy };
  }

  function isCartMutationURL(url) {
    if (!url) return false;
    return /\/cart\/(add|change|update|clear)(\.js|\?|$|\/)/.test(url);
  }

  function getFetchURL(input) {
    if (typeof input === "string") return input;
    if (input && typeof input.url === "string") return input.url;
    return "";
  }

  function hasCartUI() {
    return !!document.querySelector("cart-items, cart-drawer-items");
  }

  async function loadCart() {
    const res = await originalFetch("/cart.js", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    return await res.json();
  }

  function getSectionsToRender() {
    const sections = [];
    const mainCartItems = document.getElementById("main-cart-items");
    if (mainCartItems && mainCartItems.dataset.id) {
      sections.push({
        id: "main-cart-items",
        section: mainCartItems.dataset.id,
        selector: ".js-contents",
      });
    }
    const mainCartFooter = document.getElementById("main-cart-footer");
    if (mainCartFooter && mainCartFooter.dataset.id) {
      sections.push({
        id: "main-cart-footer",
        section: mainCartFooter.dataset.id,
        selector: ".js-contents",
      });
    }
    sections.push({
      id: "cart-icon-bubble",
      section: "cart-icon-bubble",
      selector: ".shopify-section",
    });
    sections.push({
      id: "cart-live-region-text",
      section: "cart-live-region-text",
      selector: ".shopify-section",
    });
    return sections;
  }

  function extractInnerHTML(html, selector) {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const el = doc.querySelector(selector);
      return el ? el.innerHTML : null;
    } catch (e) {
      return null;
    }
  }

  function applyThemeUpdate(parsedState) {
    if (!parsedState) return;
    const isEmpty =
      typeof parsedState.item_count === "number" &&
      parsedState.item_count === 0;
    const sections = parsedState.sections || {};
    LOG(
      "applyThemeUpdate: item_count =",
      parsedState.item_count,
      "| section keys =",
      Object.keys(sections),
    );

    const cartItemsEl = document.querySelector("cart-items");
    const cartFooterEl = document.getElementById("main-cart-footer");
    if (cartItemsEl) cartItemsEl.classList.toggle("is-empty", isEmpty);
    if (cartFooterEl) cartFooterEl.classList.toggle("is-empty", isEmpty);

    const sectionsToRender = getSectionsToRender();
    sectionsToRender.forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;
      const target = container.querySelector(section.selector) || container;
      const sourceHTML = sections[section.section];
      if (!sourceHTML) return;
      try {
        const inner = extractInnerHTML(sourceHTML, section.selector);
        if (inner !== null) target.innerHTML = inner;
      } catch (e) {
        LOG(
          "applyThemeUpdate: skip",
          section.id,
          "(no",
          section.selector,
          "in section HTML)",
        );
      }
    });

    if (cartFooterEl) cartFooterEl.style.display = isEmpty ? "none" : "";

    const cartErrors = document.getElementById("cart-errors");
    if (cartErrors) cartErrors.textContent = "";
    const cartDrawerErrors = document.getElementById("CartDrawer-CartErrors");
    if (cartDrawerErrors) cartDrawerErrors.textContent = "";
  }

  async function setBundledQty(bundle, targetQty) {
    const cart = await loadCart();
    const target = Math.max(0, targetQty);
    const sectionIds = getSectionsToRender().map((s) => s.section);

    // Only lines this script added are ours to manage. A workbook the customer
    // bought on its own has no _auto_bundle property and is left untouched.
    const parentLineKey = getParentLineKey(cart, bundle);
    const bundledLines = [];
    (cart.items || []).forEach((item, i) => {
      if (isOurLine(item, bundle)) {
        bundledLines.push({
          index: i + 1,
          qty: item.quantity,
          legacy: isLegacyLine(item, parentLineKey, bundle),
        });
      }
    });

    const currentTotal = bundledLines.reduce((sum, l) => sum + l.qty, 0);
    LOG(
      "setBundledQty: variant =",
      bundle.bundledVariantId,
      "| target =",
      target,
      "| bundled lines =",
      bundledLines.length,
      "| current total =",
      currentTotal,
    );

    if (
      bundledLines.length === 1 &&
      bundledLines[0].qty === target &&
      !bundledLines[0].legacy
    ) {
      LOG("setBundledQty: single clean line at target, skipping");
      return null;
    }

    if (bundledLines.length === 0 && target === 0) {
      LOG("setBundledQty: nothing to do (target 0, no lines)");
      return null;
    }

    let response = null;

    const sorted = bundledLines.slice().sort((a, b) => b.index - a.index);
    for (const line of sorted) {
      LOG("→ POST /cart/change.js (clear)", { line: line.index });
      const res = await originalFetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ line: line.index, quantity: 0 }),
      });
      const text = await res.text();
      try {
        response = JSON.parse(text);
      } catch (e) {
        response = null;
      }
    }

    if (target > 0) {
      // parent_line_key is what nests the free line under the digital product.
      // A cart with no parent line still gets the workbook, just unnested, which
      // beats dropping it.
      const bundledItem = {
        id: bundle.bundledVariantId,
        quantity: target,
        properties: {
          _auto_bundle: "1",
          _bundle_parent: getParentPropValue(bundle),
        },
      };
      if (parentLineKey) bundledItem.parent_line_key = parentLineKey;

      LOG("→ POST /cart/add.js (fresh)", {
        items: [{ id: bundle.bundledVariantId, quantity: target }],
        parent_line_key: parentLineKey,
      });
      const postBundledItem = (item) =>
        originalFetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({
            items: [item],
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });

      let res = await postBundledItem(bundledItem);

      // A cart that refuses the nesting would otherwise leave the customer with no
      // workbook at all, so drop the parent and add the line on its own.
      if (!res.ok && bundledItem.parent_line_key) {
        WARN(
          "bundled add rejected with parent_line_key, retrying unnested",
          res.status,
        );
        state.nestingUnsupported = true;
        delete bundledItem.parent_line_key;
        res = await postBundledItem(bundledItem);
      }

      const text = await res.text();
      LOG("bundled add response", res.status, text.slice(0, 200));
      try {
        response = JSON.parse(text);
      } catch (e) {
        response = null;
      }

      // The cart can also accept the request and drop the relationship, which
      // leaves a line this script would otherwise rebuild on every cart change.
      if (bundledItem.parent_line_key && response && response.items) {
        const added = response.items.find(
          (item) => item.variant_id === bundle.bundledVariantId,
        );
        if (added && !added.parent_relationship) {
          WARN(
            "cart did not nest the bundled line; not asking again this page load",
          );
          state.nestingUnsupported = true;
        }
      }
    }

    return response;
  }

  // Lists the bundles whose free line is out of step with the cart. A bundle
  // with neither its product nor a line of ours in the cart is skipped, so a
  // workbook bought on its own is left exactly as the customer set it. Lines of
  // ours left behind without their parent product (an update that was
  // interrupted, say) get a target of 0 and are cleared, because nothing renders
  // them any more and so nobody can remove them by hand.
  function planUpdates(cart, label) {
    const updates = [];
    BUNDLES.forEach((bundle) => {
      const triggerQty = getVariantQty(cart, bundle.triggerVariantId);
      const ours = summarizeOurLines(cart, bundle);
      if (triggerQty === 0 && ours.count === 0) return;

      const target = getTargetQty(bundle, triggerQty);
      LOG(
        label + ": bundle",
        bundle.triggerVariantId,
        "→",
        bundle.bundledVariantId,
        "| trigger qty =",
        triggerQty,
        "| our line qty =",
        ours.qty,
        "| our lines =",
        ours.count,
        "→ target =",
        target,
      );
      if (target !== ours.qty || ours.count > 1 || ours.legacy > 0) {
        updates.push({ bundle, target });
      }
    });
    return updates;
  }

  // Bundles are updated one after another rather than in parallel, because each
  // update reads the cart fresh and removes lines by their index in it.
  async function syncBundles(label) {
    const cart = await loadCart();
    LOG(
      label + ": cart.items variant ids =",
      (cart.items || []).map((i) => i.variant_id),
    );

    const updates = planUpdates(cart, label);
    if (!updates.length) {
      LOG(label + ": no bundled lines to change, leaving cart untouched");
      return cart;
    }

    showLoader();
    state.internal = true;
    let lastChanged = null;
    try {
      for (const { bundle, target } of updates) {
        const response = await setBundledQty(bundle, target);
        if (response) {
          applyThemeUpdate(response);
          lastChanged = bundle;
        }
      }
    } finally {
      state.internal = false;
      hideLoader();
    }

    const finalCart = await loadCart();
    LOG(
      label + ": post-update variant ids =",
      (finalCart.items || []).map((i) => i.variant_id),
    );

    if (
      lastChanged &&
      typeof publish === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined" &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "cart-items",
        cartData: finalCart,
        variantId: lastChanged.bundledVariantId,
      });
    }

    return finalCart;
  }

  async function reconcile() {
    LOG("reconcile: start");
    try {
      const cart = await syncBundles("reconcile");
      await hideBundledRows(cart);
      LOG("reconcile: done");
    } catch (err) {
      ERR("reconcile failed", err);
    } finally {
      hideLoader();
    }
  }

  function injectStyles() {
    if (document.getElementById("auto-bundle-styles")) return;
    const style = document.createElement("style");
    style.id = "auto-bundle-styles";
    style.textContent = `
      .cart-item.auto-bundle-hidden {
        display: none !important;
      }
      .auto-bundle-loader {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.45);
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        pointer-events: none;
        transition: opacity 150ms ease;
      }
      .auto-bundle-loader.is-visible {
        opacity: 1;
        pointer-events: auto;
      }
      .auto-bundle-loader__spinner {
        width: 48px;
        height: 48px;
        border: 4px solid rgba(255, 255, 255, 0.35);
        border-top-color: #ed1d24;
        border-radius: 50%;
        animation: auto-bundle-spin 0.8s linear infinite;
      }
      @keyframes auto-bundle-spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  function ensureLoaderEl() {
    let el = document.getElementById("auto-bundle-loader");
    if (el) return el;
    el = document.createElement("div");
    el.id = "auto-bundle-loader";
    el.className = "auto-bundle-loader";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.innerHTML = `<div class="auto-bundle-loader__spinner" aria-hidden="true"></div>`;
    document.body.appendChild(el);
    return el;
  }

  function showLoader() {
    const el = ensureLoaderEl();
    el.classList.add("is-visible");
  }

  function hideLoader() {
    const el = document.getElementById("auto-bundle-loader");
    if (el) el.classList.remove("is-visible");
  }

  function getRowLineIndex(row) {
    const input = row.querySelector(".quantity__input[data-index]");
    if (input && input.dataset.index) return Number(input.dataset.index);
    const match = (row.id || "").match(/Item-(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  // The workbook is listed inside its parent product's row now, so the bundled
  // line must never get a row of its own. main-cart-items.liquid already skips it
  // server side; this covers markup rendered somewhere that does not, such as the
  // cart drawer. A workbook the customer bought on its own keeps its row, because
  // that line carries no _auto_bundle property.
  async function hideBundledRows(cartData) {
    if (!hasCartUI()) return;

    let cart = cartData;
    if (!cart) {
      try {
        cart = await loadCart();
      } catch (e) {
        WARN("hideBundledRows: cart load failed", e);
        return;
      }
    }

    const hiddenIndexes = new Set();
    (cart.items || []).forEach((item, i) => {
      if (isAnyBundleLine(item)) hiddenIndexes.add(i + 1);
    });
    LOG("hideBundledRows: hidden line indexes =", [...hiddenIndexes]);

    document.querySelectorAll(".cart-item").forEach((row) => {
      const index = getRowLineIndex(row);
      row.classList.toggle(
        "auto-bundle-hidden",
        index !== null && hiddenIndexes.has(index),
      );
    });
  }

  window.fetch = async function (input, init) {
    const url = getFetchURL(input);
    const isMutation = isCartMutationURL(url);
    if (isMutation) {
      LOG(
        "fetch intercept: cart mutation",
        url,
        "| internal?",
        state.internal,
        "| ready?",
        state.ready,
      );
    }
    const response = await originalFetch(input, init);
    if (isMutation) {
      LOG(
        "fetch intercept: response",
        url,
        "status",
        response.status,
        "ok",
        response.ok,
      );
    }
    if (isMutation && response.ok && !state.internal && state.ready) {
      LOG("fetch intercept: queueing reconcile");
      state.queue = state.queue
        .then(reconcile)
        .catch((e) => ERR("queue err", e));
    } else if (isMutation && (state.internal || !state.ready)) {
      LOG(
        "fetch intercept: skipped reconcile (internal=" +
          state.internal +
          ", ready=" +
          state.ready +
          ")",
      );
    }
    return response;
  };

  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__auto_bundle_url = url;
    this.__auto_bundle_method = method;
    return originalXHROpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    const url = this.__auto_bundle_url || "";
    if (isCartMutationURL(url)) {
      LOG(
        "XHR intercept: cart mutation",
        this.__auto_bundle_method,
        url,
        "| internal?",
        state.internal,
        "| ready?",
        state.ready,
      );
      this.addEventListener("load", () => {
        LOG("XHR intercept: response", url, "status", this.status);
        if (
          this.status >= 200 &&
          this.status < 300 &&
          !state.internal &&
          state.ready
        ) {
          LOG("XHR intercept: queueing reconcile");
          state.queue = state.queue
            .then(reconcile)
            .catch((e) => ERR("queue err", e));
        }
      });
    }
    return originalXHRSend.apply(this, arguments);
  };

  async function init() {
    LOG("init: script loaded. BUNDLES =", BUNDLES);
    injectStyles();
    try {
      await syncBundles("init");
    } catch (e) {
      ERR("init cart sync failed", e);
      hideLoader();
    }
    state.ready = true;
    LOG("init: ready. cart UI present?", hasCartUI());
    await hideBundledRows();

    if (
      typeof subscribe === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined" &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(hideBundledRows, 50);
      });
    }

    const cartRoots = document.querySelectorAll(
      "cart-items, cart-drawer-items",
    );
    if (cartRoots.length) {
      let observerTimer = null;
      const observer = new MutationObserver(() => {
        if (observerTimer) return;
        observerTimer = setTimeout(() => {
          observerTimer = null;
          hideBundledRows();
        }, 100);
      });
      cartRoots.forEach((root) =>
        observer.observe(root, { childList: true, subtree: true }),
      );
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
